// ===================================================================
// KAIXO - Modal de denuncia
// ===================================================================
// Reutilizable: denuncia un post, mensaje, comentario o usuario.
// Carga el catálogo de motivos del backend, deja elegir uno con radio,
// permite descripción opcional y envía a /api/reports.
// ===================================================================

import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { getReportReasons, sendReport } from '../../services/api';
import './ReportModal.css';

export default function ReportModal({
    targetType,        // 'post' | 'message' | 'user' | 'comment'
    targetId,
    targetUserId,
    targetUserName = '',
    snapshot,          // { text, mediaUrls } — opcional, se guarda en la denuncia
    onClose,
    onSent
}) {
    const [reasons, setReasons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reasonId, setReasonId] = useState('');
    const [description, setDescription] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const [done, setDone] = useState(false);

    useEffect(() => {
        let cancelled = false;
        getReportReasons()
            .then(({ data }) => { if (!cancelled) setReasons(data.reasons || []); })
            .catch(() => { if (!cancelled) setErr('No se pudo cargar los motivos.'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    const submit = async () => {
        if (!reasonId) { setErr('Elige un motivo'); return; }
        setBusy(true);
        setErr('');
        try {
            await sendReport({
                targetType,
                targetId,
                targetUser: targetUserId,
                reasonId,
                description: description.trim(),
                snapshot
            });
            setDone(true);
            setTimeout(() => {
                onSent && onSent();
                onClose && onClose();
            }, 1400);
        } catch (e) {
            setErr(e?.response?.data?.error || 'Error al enviar');
        } finally {
            setBusy(false);
        }
    };

    return ReactDOM.createPortal(
        <div className="rep-overlay" onClick={onClose}>
            <div className="rep-modal" onClick={e => e.stopPropagation()}>
                <div className="rep-head">
                    <h3>Denunciar {targetType === 'post' ? 'publicación' : targetType === 'message' ? 'mensaje' : targetType === 'comment' ? 'comentario' : 'usuario'}</h3>
                    <button className="rep-close" onClick={onClose}>✕</button>
                </div>

                {targetUserName && (
                    <p className="rep-target">
                        Autor: <strong>{targetUserName}</strong>
                    </p>
                )}

                {done ? (
                    <div className="rep-done">
                        ✓ Denuncia enviada. Nuestro equipo la revisará en máximo 48 horas.
                    </div>
                ) : (
                    <>
                        <p className="rep-intro">
                            Ayúdanos a mantener KAIXO seguro. Elige el motivo que mejor describe el incumplimiento.
                            Las denuncias falsas o reiteradas pueden afectar a tu propia cuenta.
                        </p>

                        {loading && <div className="rep-loading">Cargando motivos…</div>}

                        {!loading && reasons.length > 0 && (
                            <div className="rep-reasons">
                                {reasons.map(r => (
                                    <label
                                        key={r.id}
                                        className={`rep-reason ${reasonId === r.id ? 'is-selected' : ''} rep-sev-${r.severity}`}
                                    >
                                        <input
                                            type="radio"
                                            name="reason"
                                            value={r.id}
                                            checked={reasonId === r.id}
                                            onChange={() => setReasonId(r.id)}
                                        />
                                        <div className="rep-reason-body">
                                            <div className="rep-reason-label">
                                                {r.label?.es}
                                                {r.severity === 'critical' && (
                                                    <span className="rep-sev-badge">URGENTE</span>
                                                )}
                                            </div>
                                            <div className="rep-reason-desc">{r.description?.es}</div>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        )}

                        <div className="rep-field">
                            <label>Descripción (opcional)</label>
                            <textarea
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                                placeholder="Detalles que ayuden a entender la denuncia (máx 1000 caracteres)..."
                                maxLength={1000}
                                rows={3}
                            />
                        </div>

                        {err && <div className="rep-err">{err}</div>}

                        <div className="rep-foot">
                            <button className="rep-btn-cancel" onClick={onClose} disabled={busy}>
                                Cancelar
                            </button>
                            <button className="rep-btn-send" onClick={submit} disabled={busy || !reasonId}>
                                {busy ? 'Enviando…' : 'Enviar denuncia'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>,
        document.body
    );
}
